import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';
import { useMemo } from 'react';

import { usd1PointsBunja } from '@flipster/features/src/usd1-points/bunja';
import PointsHistory from '@flipster/features/src/usd1-points/history/points-history';
import { useOverlay } from '@flipster/overlay';
import { TextButton } from '@flipster/react/design-system';
import { Modal } from '@flipster/react/design-system-legacy/modal';
import Formatted from '@flipster/react/design-utils/formatted';
import { css } from '@flipster/styled-system/css';
import { vstack } from '@flipster/styled-system/patterns';
import segment from '@flipster/utils/segment';
import { Usd1PointsEvent } from '@flipster/utils/segment/events/usd1-points';

const TotalPoints = () => {
  const overlay = useOverlay();
  const { usd1PointsAtom, isLoadingAtom, isErrorAtom } = useBunja(usd1PointsBunja);
  const usd1Points = useAtomValue(usd1PointsAtom);
  const isLoading = useAtomValue(isLoadingAtom);

  const isError = useAtomValue(isErrorAtom);

  const totalPoints = usd1Points?.totalPoints || 0;
  const { tradingPoints, holdingPoints } = useMemo(() => {
    const breakdown = usd1Points?.pointBreakdown ?? [];
    return {
      tradingPoints: breakdown.find((point) => point.pointsType === 'TRADING'),
      holdingPoints: breakdown.find((point) => point.pointsType === 'HOLDING'),
    };
  }, [usd1Points?.pointBreakdown]);

  const zeroString = isLoading || isError ? '-' : undefined;

  return (
    <div className={vstack({ w: 341, alignItems: 'center', gap: 8 })}>
      <div className={css({ textStyle: 'Text/M/Strong' })}>Your total points</div>
      <div className={css({ mt: 8 })}>
        <Formatted.Number value={totalPoints} css={{ textStyle: 'Display/XXL' }} zeroString={zeroString} />
      </div>
      <div>
        <TextButton
          color='neutral'
          size='medium'
          label='View history'
          onClick={() => {
            segment.track(Usd1PointsEvent.Usd1PageViewHistoryClick);
            overlay.open(
              <Modal>
                <Modal.Close onClose={() => overlay.close()} />
                <Modal.Header>
                  <div className={css({ textStyle: 'Title/M' })}>USD1 Points history</div>
                </Modal.Header>
                <Modal.Content>
                  <PointsHistory />
                </Modal.Content>
              </Modal>,
            );
          }}
        />
      </div>
      <dl
        className={css({
          w: '100%',
          mt: 32,
          '& dt': {
            color: 'content.default.level-4',
            textStyle: 'Text/L/Regular',
          },
          '& dd': {
            textStyle: 'Text/L/Strong',
            textAlign: 'right',
          },
          '& > div': {
            py: 16,
            display: 'flex',
            justifyContent: 'space-between',
            borderBottom: '1px solid',
            borderColor: 'border.default.level-1',
          },
        })}
      >
        <div>
          <dt className={vstack({ gap: 2, alignItems: 'flex-start' })}>
            <div>Trading points</div>
            <div
              className={css({
                color: 'content.accent.promotion',
                textStyle: 'Text/M/Regular',
              })}
            >
              Flipster bonus included
            </div>
          </dt>
          <dd className={vstack({ gap: 2, alignItems: 'flex-end' })}>
            <Formatted.Number value={tradingPoints?.points} zeroString={zeroString} />
          </dd>
        </div>
        <div>
          <dt>Holding points</dt>
          <dd>
            <Formatted.Number value={holdingPoints?.points} zeroString={zeroString} />
          </dd>
        </div>
      </dl>
    </div>
  );
};

export default TotalPoints;
